import { useMemo, useState, useEffect } from 'react';

import { useTable } from 'react-table';

import { WeightBreakdownWrapper } from './styles';
import EditableCell from '../EditableCell';

const unit = 'tons';

const tableHeaders = [
  {
    Header: `Weight per Discipline (${unit})`,
    columns: [
      {
        Header: 'Structure',
        accessor: 'weightStructure'
      },
      {
        Header: 'Mechanical',
        accessor: 'weightMechanical'
      },
      {
        Header: 'Piping',
        accessor: 'weightPiping'
      },
      {
        Header: 'Electrical',
        accessor: 'weightElectrical'
      },
      {
        Header: 'Instrument',
        accessor: 'weightInstrument'
      }
    ],
  },
  {
    Header: `Total weight (${unit})`,
    columns: [
      {
        Header: 'Dry',
        accessor: 'totalWeightDry'
      },
      {
        Header: 'Fluids',
        accessor: 'totalWeightFluids'
      },
      {
        Header: 'Operating',
        accessor: 'totalWeightOperating'
      }
    ],
  }
];


// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
  Cell: EditableCell,
};

const WeightBreakdownEdit = ({ tableData, setEditedDataField }) => {
  const [skipPageReset, setSkipPageReset] = useState(false);

  const columns = useMemo(() => tableHeaders, []);

  const Table = ({ columns, data, updateCellData, skipPageReset, editFieldProps }) => {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
      useTable({
        columns,
        data,
        defaultColumn,
        // use the skipPageReset option to disable page resetting temporarily
        autoResetPage: !skipPageReset,
        // updateCellData isn't part of the API, but
        // anything we put into these options will
        // automatically be available on the instance.
        // That way we can call this function from our
        // cell renderer!
        updateCellData,
        editFieldProps
      });

    return (
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // When our cell renderer calls updateCellData, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateCellData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setEditedDataField(columnId, value);
  };

  // After data chagnes, we turn the flag back off
  // so that if data actually changes when we're not
  // editing it, the page is reset
  useEffect(() => {
    setSkipPageReset(false);
  }, [tableData]);

  return (
    <WeightBreakdownWrapper>
      <Table
        columns={columns}
        data={[tableData]}
        updateCellData={updateCellData}
        skipPageReset={skipPageReset}
        editFieldProps={{
          weightStructure: {
            type: 'number'
          },
          weightMechanical: {
            type: 'number'
          },
          weightPiping: {
            type: 'number'
          },
          weightElectrical: {
            type: 'number'
          },
          weightInstrument: {
            type: 'number'
          },
          totalWeightDry: {
            type: 'number'
          },
          totalWeightFluids: {
            type: 'number'
          },
          totalWeightOperating: {
            type: 'number'
          }
        }}
      />
    </WeightBreakdownWrapper>
  );
};

export default WeightBreakdownEdit;
